import {
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';

import { queryKeys } from '@/lib/query-keys';
import type { Post, PostPage } from '@/lib/schemas/post';

import { interactionApi } from './api';

/**
 * Applies a change to a post everywhere it is cached — every feed variant, its
 * own detail entry, and any proposed-ideals list it appears in.
 *
 * Without this, upvoting from the feed leaves a stale count on the detail page,
 * and the reader watches their own vote disappear when they navigate.
 */
function patchPostEverywhere(
  queryClient: QueryClient,
  postId: string,
  patch: (post: Post) => Post,
): void {
  queryClient.setQueriesData<InfiniteData<PostPage>>(
    { queryKey: queryKeys.feedRoot },
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.map((item) => (item.id === postId ? patch(item) : item)),
        })),
      },
  );

  queryClient.setQueryData<Post>(queryKeys.post(postId), (post) => post && patch(post));

  queryClient.setQueriesData<Post[]>({ queryKey: ['post'] }, (posts) =>
    Array.isArray(posts) ? posts.map((item) => (item.id === postId ? patch(item) : item)) : posts,
  );

  queryClient.setQueryData<Post[]>(queryKeys.bookmarks, (posts) =>
    posts?.map((item) => (item.id === postId ? patch(item) : item)),
  );
}

/**
 * Upvoting, applied optimistically.
 *
 * The count moves the instant the button is pressed, because waiting ~150ms for
 * a round trip makes the control feel broken. If the request fails, the previous
 * state is restored — captured before the optimistic write rather than
 * reconstructed afterwards, so a double-click cannot leave the count drifted.
 */
export function useTogglePostVote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, hasUpvoted }: { postId: string; hasUpvoted: boolean }) =>
      hasUpvoted ? interactionApi.removePostUpvote(postId) : interactionApi.upvotePost(postId),

    onMutate: async ({ postId, hasUpvoted }) => {
      // Stop any in-flight refetch from landing on top of the optimistic write.
      await queryClient.cancelQueries({ queryKey: queryKeys.post(postId) });

      const delta = hasUpvoted ? -1 : 1;

      patchPostEverywhere(queryClient, postId, (post) => ({
        ...post,
        hasUpvoted: !hasUpvoted,
        upvoteCount: Math.max(0, post.upvoteCount + delta),
      }));

      return { delta, hasUpvoted };
    },

    onError: (_error, variables, context) => {
      // Undo by applying the inverse delta, rather than restoring a snapshot.
      //
      // A snapshot would have to come from somewhere, and the obvious source —
      // the `['post', id]` cache entry — does not exist when the reader votes
      // from the feed, which is the common case. Reversing the delta works
      // wherever the post is cached and needs nothing to have been captured.
      const delta = context?.delta ?? 0;

      patchPostEverywhere(queryClient, variables.postId, (post) => ({
        ...post,
        hasUpvoted: context?.hasUpvoted ?? post.hasUpvoted,
        upvoteCount: Math.max(0, post.upvoteCount - delta),
      }));
    },

    onSuccess: (result, variables) => {
      // Settle on the server's count: other people have been voting too, so the
      // optimistic ±1 is a guess and this is the truth.
      patchPostEverywhere(queryClient, variables.postId, (post) => ({
        ...post,
        hasUpvoted: result.hasUpvoted,
        upvoteCount: result.upvoteCount,
      }));
    },
  });
}

export function useToggleBookmark() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ postId, hasBookmarked }: { postId: string; hasBookmarked: boolean }) =>
      hasBookmarked ? interactionApi.removeBookmark(postId) : interactionApi.bookmark(postId),

    onMutate: ({ postId, hasBookmarked }) => {
      patchPostEverywhere(queryClient, postId, (post) => ({
        ...post,
        hasBookmarked: !hasBookmarked,
      }));
      return { hasBookmarked };
    },

    onError: (_error, variables, context) => {
      patchPostEverywhere(queryClient, variables.postId, (post) => ({
        ...post,
        hasBookmarked: context?.hasBookmarked ?? post.hasBookmarked,
      }));
    },

    onSettled: () => {
      // The saved list itself is a different query; it has to be refetched.
      void queryClient.invalidateQueries({ queryKey: queryKeys.bookmarks });
    },
  });
}
